import { handleIncomingShare } from './incoming-share';
import { subscribeIncomingShareImages } from './incoming-share-image';

const IMAGE_MIME_TYPES = new Set(['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/heic', 'image/heif']);

const MIME_BY_EXTENSION: Record<string, string> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
  heic: 'image/heic',
  heif: 'image/heif',
  pdf: 'application/pdf'
};

export type DroppedShareFile = {
  kind: 'image' | 'pdf';
  uri: string;
  name: string;
  mimeType: string;
};

export type DroppedShareText = {
  kind: 'text';
  text: string;
};

export type DroppedShare = {
  id: string;
  items: Array<DroppedShareFile | DroppedShareText>;
  blobUrls: string[];
};

type DropData = {
  files?: ArrayLike<File>;
  getData?: (type: string) => string;
};

const blobs = new Map<string, string[]>();
let watching = false;
let unsubscribe = () => {};

const canonicalMimeType = (mimeType: string) => {
  const normalized = mimeType.split(';')[0].trim().toLowerCase();
  return normalized === 'image/jpg' ? 'image/jpeg' : normalized;
};

const mimeOf = (file: File) => {
  const fromType = canonicalMimeType(typeof file.type === 'string' ? file.type : '');
  if (fromType) return fromType;
  const extension = (file.name || '').split('.').pop() || '';
  return MIME_BY_EXTENSION[extension.toLowerCase()] || '';
};

const kindOf = (mimeType: string): 'image' | 'pdf' | null => {
  if (mimeType === 'application/pdf') return 'pdf';
  if (IMAGE_MIME_TYPES.has(mimeType)) return 'image';
  return null;
};

const fileName = (name: string, kind: 'image' | 'pdf') => {
  const trimmed = typeof name === 'string' ? name.trim() : '';
  return trimmed || (kind === 'pdf' ? 'document.pdf' : 'image');
};

const readText = (data: DropData) => {
  try {
    const uriList = data.getData ? data.getData('text/uri-list') : '';
    const plain = data.getData ? data.getData('text/plain') : '';
    return (uriList || plain || '').trim();
  } catch (error) {
    return '';
  }
};

const createId = () => `web-drop-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

const release = (id: string) => {
  const urls = blobs.get(id);
  if (!urls) return;
  blobs.delete(id);
  urls.forEach(url => {
    try {
      URL.revokeObjectURL(url);
    } catch (error) {
      // The preview is already gone.
    }
  });
};

const watchDismissal = () => {
  if (watching) return;
  watching = true;
  unsubscribe = subscribeIncomingShareImages(preview => {
    // Closing the shortcut must not revoke the files. OCR still has to read them.
    if (!preview) return;
    Array.from(blobs.keys()).forEach(id => {
      if (id !== preview.id) release(id);
    });
  });
};

/** Drop the blob URLs for a share once its files have been copied into memory. */
export const releaseDroppedShare = (id: string) => {
  release(id);
};

/** File drags and link drags. A text selection dragged inside the page is ignored. */
export const isExternalDrop = (types: ArrayLike<string> | undefined | null) => {
  if (!types) return false;
  return Array.from(types).some(type => type === 'Files' || type === 'text/uri-list');
};

/** The same documents the native share sheet accepts, addressed by blob URLs. */
export const shareFromDataTransfer = (
  data: DropData | null | undefined,
  createObjectURL: (file: Blob) => string = file => URL.createObjectURL(file)
): DroppedShare | null => {
  if (!data) return null;

  const files = data.files ? Array.from(data.files) : [];
  const items: DroppedShareFile[] = [];
  const blobUrls: string[] = [];

  files.forEach(file => {
    const mimeType = mimeOf(file);
    const kind = kindOf(mimeType);
    if (!kind) return;
    const uri = createObjectURL(file);
    blobUrls.push(uri);
    items.push({ kind, uri, name: fileName(file.name, kind), mimeType });
  });

  if (!items.length) {
    if (files.length) return null;
    const text = readText(data);
    if (!text) return null;
    return { id: createId(), items: [{ kind: 'text', text }], blobUrls: [] };
  }

  return { id: createId(), items, blobUrls };
};

/** Show the shortcut for a browser drop, and release blob URLs once it leaves the screen. */
export const presentDroppedShare = (share: DroppedShare) => {
  watchDismissal();
  const dropped = handleIncomingShare(share);
  blobs.set(share.id, share.blobUrls);
  (Array.isArray(dropped) ? dropped : []).forEach(item => {
    if (item && item.id) release(item.id);
  });
};

export const resetDroppedSharesForTests = () => {
  Array.from(blobs.keys()).forEach(release);
  if (watching) unsubscribe();
  watching = false;
  unsubscribe = () => {};
};
